import {
  AutomationActionTemplate,
  IAutomationTemplate,
  ITaskTemplate,
  isEventDrivenTiming
} from '@dadohr/shared/domainTypes/experience';
import { getItemDisplayName } from '../../../helpers/adminName';
import { interpolateExperienceText } from '../../../../../services/set/interpolation';
import styled from '@emotion/styled';
import { Divider, Typography } from '@material-ui/core';
import { AlarmClock, Calendar, User, Zap } from 'lucide-react';
import moment from 'moment-timezone';
import { WithShape } from '../../../../../components/Charts/Util';
import { WeeksAndDaysDuration } from '../../../../../components/WeeksAndDaysDuration';
import { useUnsafeTranslation } from '../../../../../i18next';
import { AutomationStats } from '../../../../../services/experiences/automation';
import { TaskStats } from '../../../../../services/experiences/task';
import { ONE_DAY } from '../../../../../services/time';
import { ActorDescriptor } from '../../../components/Actors';
import { useActionDefinition } from '../../../components/Builders/AutomationBuilder/actionHelpers';
import { AutomationStatsChips, TaskStatChips } from '../StatChips';

type TaskProps = {
  tpl: ITaskTemplate;
  stats: TaskStats | null;
  actor: ActorDescriptor | undefined;
  expTitle?: string;
  orgName?: string;
};

type AutomationProps = {
  tpl: IAutomationTemplate;
  stats: AutomationStats | null;
};

const Body = styled('div')((p) => ({
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(3)}px`,
  minWidth: 320
}));

const Heading = styled('div')((p) => ({
  fontSize: p.theme.custom.fontSize.xs,
  color: p.theme.custom.colors.gray.w500,
  textTransform: 'uppercase'
}));

const Row = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  gap: p.theme.spacing(3),
  marginTop: p.theme.spacing()
}));

const IconAndText = styled('div')((p) => ({
  fontSize: p.theme.custom.fontSize.m,
  color: p.theme.custom.colors.gray.w500,
  display: 'flex',
  alignItems: 'center',
  gap: p.theme.spacing(0.5)
}));

const ICON_SIZE = 16;

const DividerContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2),
  marginBottom: p.theme.spacing(2),
  marginLeft: -p.theme.spacing(3), // negative margin to go full width with the divider
  marginRight: -p.theme.spacing(3)
}));

export const TaskTimelineTaskPopoverBody = ({
  tpl,
  stats,
  actor,
  expTitle,
  orgName
}: TaskProps) => {
  const duration =
    tpl.timing.type === 'fixed'
      ? tpl.timing.end - tpl.timing.start
      : tpl.timing.end.duration; // for this purpose both are treated the same - we could use different language around it though

  const { t } = useUnsafeTranslation();
  const displayTitle = expTitle
    ? interpolateExperienceText(
        getItemDisplayName(tpl),
        {
          experience: { vars: { title: expTitle, tasks: {} } },
          org: { vars: { name: orgName || '' } },
          roles: []
        },
        { dateFormat: 'YYYY/MM/DD' }
      )
    : getItemDisplayName(tpl);

  return (
    <Body>
      <Heading>{t('Task')}</Heading>
      <Typography variant="h5">{displayTitle}</Typography>
      {actor && (
        <Row>
          <IconAndText>
            <User size={ICON_SIZE} />
            <WithShape shape="circle" color={actor.color}>
              {actor.label}
            </WithShape>
          </IconAndText>

          <IconAndText>
            <Calendar size={ICON_SIZE} />
            {tpl.timing.type === 'fixed' && (
              <>
                {t('Day {{day}}', {
                  day: Math.round(tpl.timing.start / ONE_DAY) + 1
                })}
                {tpl.timing.time && (
                  <span>
                    {' ' + t('at') + ' '}
                    {moment()
                      .hour(tpl.timing.time.hour)
                      .minute(tpl.timing.time.minute)
                      .format('hh:mm a')}
                  </span>
                )}
              </>
            )}
            {tpl.timing.type === 'triggered' && (
              <span>
                {t('After completion of another task')}
              </span> /* TODO Triggered Items - don't have access to other task title here */
            )}
            {tpl.timing.type === 'dataChange' && (
              <span>
                {t('When data changes')}
              </span>
            )}
          </IconAndText>

          {duration !== null && (
            <IconAndText>
              <AlarmClock size={ICON_SIZE} />
              <WeeksAndDaysDuration days={duration / ONE_DAY} />
            </IconAndText>
          )}
        </Row>
      )}
      {stats && (
        <>
          <DividerContainer>
            <Divider light />
          </DividerContainer>
          <TaskStatChips stats={stats} />
        </>
      )}
    </Body>
  );
};

const ActionLabel = ({ tpl }: { tpl: AutomationActionTemplate }) => {
  const def = useActionDefinition(tpl);
  return <div>{def.label}</div>;
};

export const TaskTimelineAutomationPopoverBody = ({
  tpl,
  stats
}: AutomationProps) => {
  const { t } = useUnsafeTranslation();
  return (
    <Body>
      <Heading>{t('Automation')}</Heading>
      <Typography variant="h5">{tpl.title}</Typography>
      {tpl.actions.length === 1 && (
        <Row>
          <IconAndText>
            <Zap size={ICON_SIZE} />
            <ActionLabel tpl={tpl.actions[0]} />
          </IconAndText>
        </Row>
      )}
      {tpl.actions.length > 1 && (
        <Row>
          <IconAndText>
            <Zap size={ICON_SIZE} />
            <div>{t('{{ count }} actions', { count: tpl.actions.length })}</div>
          </IconAndText>
        </Row>
      )}
      {stats && (
        <>
          <DividerContainer>
            <Divider light />
          </DividerContainer>
          <AutomationStatsChips stats={stats} />
        </>
      )}
    </Body>
  );
};
